<template>
  <div class="comparison-ctn">
    <!-- Phones get a stacked list rather than the 3-column table: 25 rows against
         two plans cannot be read side by side at 375px, and a horizontally
         scrolling table defeats the point of a comparison. Same data, same
         source, different shape — the pattern Products.vue already uses to
         reshape its own table below smAndDown. -->
    <div v-if="isMobile" class="comparison-stack">
      <div class="stack-plans">
        <div class="stack-plan stack-plan--accent">
          <span class="plan-header-name">{{ dppTextileName }}</span>
          <span class="plan-header-price">
            <v-progress-circular v-if="loading" size="20" indeterminate color="primary" role="status" :aria-label="loadingLabel"></v-progress-circular>
            <template v-else>
              <span class="plan-header-amount">{{ dppTextilePrice }}</span>
              <span v-if="dppTextilePricePeriod" class="plan-header-period">{{ dppTextilePricePeriod }}</span>
            </template>
          </span>
          <v-expand-transition>
            <span v-if="hasPriceNote" class="content-hint plan-header-note">{{ dppTextilePriceNote }}</span>
          </v-expand-transition>
          <v-btn
            block
            depressed
            color="primary"
            :disabled="dppTextileCtaDisabled"
            @click="$emit('dpp-textile-cta')">{{ dppTextileCtaLabel }}</v-btn>
        </div>
        <div class="stack-plan">
          <span class="plan-header-name">{{ enterpriseName }}</span>
          <span class="plan-header-price plan-header-price--custom">
            <span class="plan-header-custom">{{ enterprisePrice }}</span>
          </span>
          <v-btn
            block
            outlined
            color="primary"
            @click="$emit('enterprise-cta')">{{ enterpriseCtaLabel }}</v-btn>
        </div>
      </div>

      <section v-for="group in groups" :key="group.title" class="stack-group">
        <h6 class="group-title">{{ group.title }}</h6>
        <dl class="stack-rows">
          <div v-for="row in group.rows" :key="row.label" class="stack-row">
            <dt class="stack-feature">{{ row.label }}</dt>
            <dd class="stack-value">
              <span class="stack-plan-label">{{ dppTextileName }}</span>
              <plan-feature-value :value="row.dppTextile" :includedLabel="includedLabel" :notIncludedLabel="notIncludedLabel"></plan-feature-value>
            </dd>
            <dd class="stack-value">
              <span class="stack-plan-label">{{ enterpriseName }}</span>
              <plan-feature-value :value="row.enterprise" :includedLabel="includedLabel" :notIncludedLabel="notIncludedLabel"></plan-feature-value>
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <div v-else class="comparison-scroll" tabindex="0" role="region" :aria-label="caption">
      <table class="comparison">
        <caption class="d-sr-only">{{ caption }}</caption>
        <colgroup>
          <col class="col-feature">
          <col class="col-plan">
          <col class="col-plan">
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="header-spacer" aria-hidden="true"></th>
            <th scope="col" class="plan-header plan-header--accent" :aria-label="dppTextileName">
              <span class="plan-header-name">{{ dppTextileName }}</span>
              <span class="plan-header-price">
                <v-progress-circular v-if="loading" size="20" indeterminate color="primary" role="status" :aria-label="loadingLabel"></v-progress-circular>
                <template v-else>
                  <span class="plan-header-amount">{{ dppTextilePrice }}</span>
                  <span v-if="dppTextilePricePeriod" class="plan-header-period">{{ dppTextilePricePeriod }}</span>
                </template>
              </span>
              <v-expand-transition>
                <span v-if="hasPriceNote" class="content-hint plan-header-note">{{ dppTextilePriceNote }}</span>
              </v-expand-transition>
              <v-btn
                depressed
                color="primary"
                :disabled="dppTextileCtaDisabled"
                @click="$emit('dpp-textile-cta')">{{ dppTextileCtaLabel }}</v-btn>
            </th>
            <th scope="col" class="plan-header" :aria-label="enterpriseName">
              <span class="plan-header-name">{{ enterpriseName }}</span>
              <span class="plan-header-price plan-header-price--custom">
                <span class="plan-header-custom">{{ enterprisePrice }}</span>
              </span>
              <v-btn
                outlined
                color="primary"
                @click="$emit('enterprise-cta')">{{ enterpriseCtaLabel }}</v-btn>
            </th>
          </tr>
        </thead>
        <tbody v-for="group in groups" :key="group.title">
          <tr>
            <th colspan="3" scope="rowgroup" class="group-title">{{ group.title }}</th>
          </tr>
          <tr v-for="row in group.rows" :key="row.label" class="feature-row">
            <th scope="row" class="feature-label">{{ row.label }}</th>
            <td class="feature-value">
              <plan-feature-value :value="row.dppTextile" :includedLabel="includedLabel" :notIncludedLabel="notIncludedLabel"></plan-feature-value>
            </td>
            <td class="feature-value">
              <plan-feature-value :value="row.enterprise" :includedLabel="includedLabel" :notIncludedLabel="notIncludedLabel"></plan-feature-value>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>

import PlanFeatureValue from '@/components/upgrade/PlanFeatureValue'

export default {
  name: 'PlanComparisonTable',
  components: { PlanFeatureValue },
  props: {
    groups: {
      type: Array,
      required: true
    },
    caption: {
      type: String,
      required: true
    },
    includedLabel: {
      type: String,
      required: true
    },
    notIncludedLabel: {
      type: String,
      required: true
    },
    dppTextileName: {
      type: String,
      required: true
    },
    dppTextilePrice: {
      type: String,
      default: ''
    },
    dppTextilePricePeriod: {
      type: String,
      default: ''
    },
    dppTextilePriceNote: {
      type: String,
      default: ''
    },
    dppTextileCtaLabel: {
      type: String,
      required: true
    },
    dppTextileCtaDisabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingLabel: {
      type: String,
      required: true
    },
    enterpriseName: {
      type: String,
      required: true
    },
    enterprisePrice: {
      type: String,
      required: true
    },
    enterpriseCtaLabel: {
      type: String,
      required: true
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.smAndDown
    },

    hasPriceNote () {
      return Boolean(this.dppTextilePriceNote)
    }
  }
}
</script>

<style lang="sass" scoped>
@import '@/variables.sass'

$comparison-underline: #dadada

.comparison-stack
  .stack-plans
    display: flex
    flex-direction: column
    gap: 20px
    padding-bottom: 20px

  .stack-plan
    padding-bottom: 18px
    border-bottom: 2px solid $comparison-underline
    &--accent
      border-bottom-color: $primary

  .group-title
    padding: 16px 0 6px

  .stack-rows
    margin: 0

  .stack-row
    padding: 12px 0
    border-top: 1px solid $divider-grey

  .stack-feature
    font-size: 14px
    line-height: 20px
    color: $text-black

  .stack-value
    display: flex
    align-items: center
    justify-content: space-between
    margin: 6px 0 0
    font-size: 14px
    line-height: 20px
    color: $text-black

  .stack-plan-label
    color: #777

.comparison-ctn
  max-width: 980px
  margin-left: auto
  margin-right: auto
  padding: 20px
  background: white
  border-radius: 6px
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.1)

.comparison-scroll
  overflow-x: auto

.comparison
  width: 100%
  min-width: 640px
  table-layout: fixed
  border-collapse: collapse

.col-feature
  width: 44%

.col-plan
  width: 28%

.header-spacer
  border: 0

.plan-header
  position: relative
  padding: 12px 14px 50px
  text-align: left
  font-weight: 400
  vertical-align: top
  border-bottom: 2px solid $comparison-underline
  &--accent
    border-bottom-color: $primary
  .v-btn
    position: absolute
    right: 14px
    bottom: 14px
    left: 14px
    width: auto
    padding: 0 8px
    font-size: 13px
    letter-spacing: 0.5px

.plan-header-name
  display: block
  font-size: 16px
  font-weight: 500
  line-height: 20px
  letter-spacing: 0.2px
  color: $text-black

.plan-header-price
  display: flex
  align-items: baseline
  min-height: 34px
  margin: 10px 0
  color: $text-black
  &--custom
    align-items: center

.plan-header-amount
  font-size: 26px
  font-weight: 700
  line-height: 34px
  letter-spacing: 0.7px

.plan-header-period
  margin-left: 4px
  font-size: 12px
  line-height: 20px

.plan-header-custom
  font-size: 16px
  line-height: 20px

.plan-header-note
  display: block
  min-height: 16px
  padding-bottom: 10px
  font-size: 12px
  line-height: normal

.group-title
  padding: 16px 0 6px
  text-align: left
  font-size: 16px
  font-weight: 600
  letter-spacing: 0.8px
  color: $text-black

.feature-row
  th, td
    border-top: 1px solid $divider-grey

.feature-label
  padding: 11px 14px 11px 0
  text-align: left
  font-size: 14px
  font-weight: 400
  line-height: 20px
  color: $text-black

.feature-value
  padding: 11px 14px
  font-size: 14px
  line-height: 20px
  color: $text-black

</style>
